import PropTypes from 'prop-types';
import React, { Component, Children, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Trans, useTranslation, withTranslation } from 'react-i18next';
import dayjs from '@common/utils/dayjs';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@common/components/ui/hover-card';
import { useIsComponentEnabledForUserNetworks } from '@common/hooks/use-is-component-enabled';
import { EComponentTypes } from '@common/definitions';
import { getUserNetworkIds } from '@common/utils/has-enabled-component';
import ImageItem from '../image-item/large';
import { Button } from '../button';
import * as usersSelector from '../../../modules/core/selectors/users';
import fetchUser from '../../../modules/core/actions/fetch-user';

export function useViewUser({
  match, history, loggedUserId, network, user,
}) {
  return useCallback(() => {
    const isCurrent = Number(user.id) === loggedUserId;
    if (match.path.startsWith('/admin')) {
      history.push(isCurrent ? '/admin/profile/about' : `/admin/users/${user.id}/about`);
    } else if (network) {
      history.push(isCurrent ? '/profile/about' : `/users/${user.id}`);
    }
  }, [match, history, loggedUserId, network, user]);
}

export function PopoverContent(props) {
  const {
    user, loggedUserId, history,
  } = props;

  const { t } = useTranslation();
  const isChatEnabled = useIsComponentEnabledForUserNetworks(
    EComponentTypes.CHAT,
    getUserNetworkIds(user),
  );

  const handleViewUser = useViewUser(props);

  const handleOpenChat = () => {
    history.push(`/conversations/users/${user.id}`);
  };

  return (
    <>
      <ImageItem
        imageSize={50}
        image={user.profile_img}
        name={user.full_name}
      >
        <small>
          {[
            user.function,
            user.last_active
            && t('common:profile_popover_last_seen', {
              lastSeen: dayjs(user.last_active).fromNow(),
            }),
          ].filter((s) => !!s).join(' - ')}
        </small>
      </ImageItem>
      <div className="ProfilePopover__Actions">
        <Button type="primary" size="small" onClick={handleViewUser}>
          <Trans i18nKey="common:profile_popover_show_profile" />
        </Button>
        {isChatEnabled && user.id !== loggedUserId && user.can_chat && (
          <Button type="primary" size="small" onClick={handleOpenChat}>
            <Trans i18nKey="common:profile_popover_send_private_message" />
          </Button>
        )}
      </div>
    </>
  );
}

class ProfilePopover extends Component {
  static propTypes = {
    match: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    children: PropTypes.element.isRequired,
    user: PropTypes.object.isRequired,
    loggedUserId: PropTypes.string.isRequired,
    t: PropTypes.func.isRequired,
    network: PropTypes.string,
    className: PropTypes.string,
  };

  constructor() {
    super();

    this.state = {
      useAnchor: false,
    };

    this.setReference = (ref) => {
      if (ref && !this.state.useAnchor) {
        let element = ref.parentElement;
        let foundAnchor = false;
        while (element && element !== document.body) {
          if (element.tagName === 'A') {
            foundAnchor = true;
            break;
          }
          element = element.parentElement;
        }
        if (!foundAnchor) {
          this.setState({ useAnchor: true });
        }
      }
    };

    this.handleOpenChange = (open) => {
      const { dispatch, user } = this.props;
      if (open && user?.should_load) dispatch(fetchUser(user.id));
    };
  }

  render() {
    const { children, user, className } = this.props;

    if (!user) return children;

    const WrapperComponent = this.state.useAnchor ? 'a' : 'span';

    return (
      <HoverCard openDelay={500} closeDelay={500} onOpenChange={this.handleOpenChange}>
        <HoverCardTrigger asChild>
          <WrapperComponent
            ref={this.setReference}
            className={className ? `ProfilePopover__Trigger ${className}` : 'ProfilePopover__Trigger'}
          >
            {Children.only(children)}
          </WrapperComponent>
        </HoverCardTrigger>
        {!user.should_load && (
          <HoverCardContent
            side="top"
            align="center"
            id={`user-popover-${user.id}`}
            className="ProfilePopover fs-exclude tw-w-[350px] tw-max-w-[350px] tw-p-3"
          >
            <PopoverContent {...this.props} />
          </HoverCardContent>
        )}
      </HoverCard>
    );
  }
}

const mapStateToProps = (state, props) => ({
  user: usersSelector.byId(state, props.user.id) || props.user,
  network: state.network.selected,
  loggedUserId: state.loggedUser.user.id,
});

export default withRouter(withTranslation()(connect(mapStateToProps)(ProfilePopover)));
